import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AsYouType } from 'libphonenumber-js';
import { isNumberMobileAndValid } from '../../helpers/phoneNumberHelper';
import nextLoading from '../../assets/images/next-loading.svg';
import OtpInput from 'react18-input-otp';
import { firebaseAnalyticsNO } from '../../firebase';
import { eventNames } from 'configuration/eventNames';
import { ORDER_STAGE_TYPES } from 'helpers/enums';
import { BackButtonCWUI } from '@next-order/next-order-components';
import { UserLoginRegistrationContext } from 'contexts/UserLoginRegistrationProvider';
import { API_URL } from 'configuration/urls';
import { TableBookingViewContext } from 'contexts/TableBookingViewProvider';
import { useAuth } from 'contexts/AuthProvider';

const NO_OF_OTP_INPUTS = 6;

export const OTP_CHANNELS = {
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
};

function WhatsAppGlyph({ className }) {
  return (
    <svg
      viewBox='0 0 24 24'
      aria-hidden='true'
      focusable='false'
      className={className}
      fill='currentColor'
    >
      <path d='M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35M12.04 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37A9.86 9.86 0 0 1 2.15 12c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 7 2.9 9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.9 9.88m8.42-18.3A11.8 11.8 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.9c0 2.1.55 4.14 1.59 5.94L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.9-11.9a11.8 11.8 0 0 0-3.49-8.42' />
    </svg>
  );
}

function CheckGlyph({ className }) {
  return (
    <svg
      viewBox='0 0 20 20'
      aria-hidden='true'
      focusable='false'
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth='2.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M4 10.5l4 4 8-9' />
    </svg>
  );
}

const RESEND_BUTTON_BASE =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-4 text-[15px] font-semibold leading-5 tracking-[-0.01em] ' +
  'transition-[background-color,color,border-color,opacity,transform] duration-200 ease-out active:scale-[0.98] ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white ' +
  'disabled:pointer-events-none disabled:opacity-50';

export default function VerificationCodeComponent({
  currentOrder,
  inLayoutFlow,
  updateOrderStageBack,
  storeConfig,
  sidebarAnimationClass,
}) {
  const {
    localState,
    setLocalState,
    updateOrderStageHandler,
    validateMobileNumber,
    handleVerificationCodeError,
  } = useContext(UserLoginRegistrationContext);
  const loginType = useContext(TableBookingViewContext)[0]?.loginType;
  const { isOauthFlow } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [isNumberEditable, setIsNumberEditable] = useState(false);
  const [errors, setErrors] = useState({});
  const [mobileNumberEdited, setMobileNumberEdited] = useState('');
  const [displayMobileNumberEdited, setDisplayMobileNumberEdited] =
    useState('');

  const editMobileNumberRef = useRef(null);
  const otpRef = useRef(null);
  const COUNTRY_IDENTIFIER = storeConfig.countryIdentifier;

  useEffect(() => {
    if (
      currentOrder?.stage === ORDER_STAGE_TYPES.VERIFY_FROM ||
      inLayoutFlow === true
    ) {
      otpRef?.current?.focusInput?.(0);
    }
  }, [currentOrder?.stage, inLayoutFlow]);

  useEffect(() => {
    if (
      currentOrder.stage === ORDER_STAGE_TYPES.VERIFY_FROM ||
      inLayoutFlow === true
    ) {
      const firstInput = document.getElementsByClassName(
        'verification-otp-input'
      )[0];
      firstInput?.focus();
      setMobileNumberEdited(localState.displayMobileNumber);
    }
  }, [currentOrder.stage, localState.displayMobileNumber, inLayoutFlow]);

  useEffect(() => {
    if (!!localState.verificationCodeError) {
      otpRef.current.focusInput(0);
      setVerificationCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localState.verificationCodeError]);

  /////////////////////////////////////////////////////////////
  // need to remove when Newzealand NZ sms issue is resolved
  useEffect(() => {
    if (COUNTRY_IDENTIFIER?.toLowerCase() === 'nz') {
      const tempCodeForNZ = '123456';
      setVerificationCode(tempCodeForNZ);
      handleVerificationCodeNextStep(tempCodeForNZ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [COUNTRY_IDENTIFIER]);
  // need to remove when Newzealand NZ sms issue is resolved
  /////////////////////////////////////////////////////////////

  useEffect(() => {
    let numberAsUserType = new AsYouType(COUNTRY_IDENTIFIER).input(
      mobileNumberEdited
    );
    setDisplayMobileNumberEdited(numberAsUserType);
    const { error } = isNumberMobileAndValid(
      numberAsUserType,
      COUNTRY_IDENTIFIER
    );
    setErrors({
      mobileNumberEdited: error,
    });
  }, [COUNTRY_IDENTIFIER, mobileNumberEdited]);

  useEffect(() => {
    if (isNumberEditable) {
      editMobileNumberRef.current?.focus();
    }
  }, [isNumberEditable]);

  useEffect(() => {
    if (verificationCode.length === NO_OF_OTP_INPUTS) {
      handleVerificationCodeSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  const handleMobileNumberChangeInVerification = (number) => {
    if (number !== localState.validatedNumber) {
      setLocalState({
        shouldResendCodeOnNumberEdit: true,
      });

      validateMobileNumber(number);
    }
  };

  const handleEditMobileNumber = () => {
    if (!isNumberEditable) {
      setIsNumberEditable(true);
    } else {
      setIsNumberEditable(false);
      handleMobileNumberChangeInVerification(mobileNumberEdited);
    }
  };

  const handleMobileNumberEditedOnChange = (e) => {
    setMobileNumberEdited(e.target.value);
  };

  const handleVerificationCodeNextStep = (verificationCode) => {
    if (verificationCode.length < 6) {
      handleVerificationCodeError({
        err: 'Verification Code is less than 6 digits',
        verificationCodeEntered: verificationCode,
        mobileNumber: localState.validatedNumber,
      });
    } else {
      updateOrderStageHandler(
        ORDER_STAGE_TYPES.REVIEW,
        verificationCode,
        loginType
      );
    }
  };

  const handleVerificationCodeSubmit = () => {
    firebaseAnalyticsNO.logEvent(eventNames.ENTERED_OPT);
    handleVerificationCodeNextStep(verificationCode);
  };

  const resendOTP = async (
    mobileNumberToResendTo,
    channel = OTP_CHANNELS.SMS
  ) => {
    setLocalState({
      isLoading: true,
      otpChannel: channel,
      resendError: '',
    });
    let { restaurantId, storeId } = storeConfig;
    const requestObj = {
      mobileNumber: mobileNumberToResendTo,
      restaurantId,
      storeId,
      // Delivery channel flag consumed by api-service / guftgu-service.
      ...(channel === OTP_CHANNELS.WHATSAPP ? { otpChannel: channel } : {}),
    };
    const fallbackError =
      channel === OTP_CHANNELS.WHATSAPP
        ? 'We couldn\u2019t send the code on WhatsApp. Try again or resend by SMS.'
        : 'We couldn\u2019t resend the code. Please try again.';

    try {
      const response = await fetch(
        `${API_URL}api/v1/${isOauthFlow ? 'resendMobileOTP' : 'resendOTP'}`,
        {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestObj),
        }
      );
      const responseData = await response.json();
      const hasErrors = !response.ok || responseData?.errors?.length > 0;

      setLocalState({
        isCodeResent: !hasErrors ? true : localState.isCodeResent,
        authCodeCounter: !hasErrors ? 30 : localState.authCodeCounter,
        resendError: hasErrors
          ? responseData?.errors?.[0] || fallbackError
          : '',
        isLoading: false,
      });
    } catch (err) {
      console.error('Error resending OTP:', err);
      setLocalState({ resendError: fallbackError, isLoading: false });
    }
  };

  const handleResendButtonClick = (channel) => () => {
    resendOTP(localState.validatedNumber, channel);
    const firstInput = document.getElementsByClassName(
      'verification-otp-input'
    )[0];
    firstInput?.focus();
    setVerificationCode('');
  };

  const isResendLocked = !!localState.authCodeCounter;
  const sentViaSms =
    localState.isCodeResent && localState.otpChannel !== OTP_CHANNELS.WHATSAPP;
  const sentViaWhatsApp =
    localState.isCodeResent && localState.otpChannel === OTP_CHANNELS.WHATSAPP;

  const handleChange = useCallback(
    (code) => setVerificationCode(code),
    [setVerificationCode]
  );

  return (
    <div
      className={`${sidebarAnimationClass} detailsConfirm detailMobileVer active`}
    >
      <BackButtonCWUI onClick={updateOrderStageBack} />
      <div
        className='detailsTitle hidden-xs hidden-sm'
        style={{ marginTop: 0 }}
      >
        Thanks {localState.firstName.trim()}, we just need to verify your mobile
      </div>
      <div
        className='detailsTitle visible-xs visible-sm'
        style={{ marginTop: 0 }}
      >
        We just need to verify {localState.displayMobileNumber}
      </div>
      <div className='mobVerImp'>
        <span>Verification Code</span>
        {(currentOrder.stage === ORDER_STAGE_TYPES.VERIFY_FROM ||
          inLayoutFlow === true) && (
          <>
            <OtpInput
              ref={otpRef}
              value={verificationCode}
              onChange={handleChange}
              numInputs={NO_OF_OTP_INPUTS}
              isInputNum
              shouldAutoFocus
              containerStyle='verification-code-container'
              inputStyle={'verification-otp-input text-2xl text-bold'}
              focusStyle={'verification-otp-input-focus'}
              errorStyle={'verification-otp-input-error'}
              hasErrored={!!localState.verificationCodeError}
              isDisabled={false}
              renderInput={(props) => <input {...props} />}
              autoComplete='one-time-code'
            />
            {!!localState.verificationCodeError && (
              <p className='text-red-600'>Wrong code. Try again.</p>
            )}
          </>
        )}
        {
          /////////////////////////////////////////////////////////////
          // need to remove the nz check when Newzealand NZ sms issue is resolved
          COUNTRY_IDENTIFIER?.toLowerCase() !== 'nz' && (
            <div className='mt-7 flex flex-col gap-3'>
              <p
                className='text-[13px] leading-[18px] text-neutral-525'
                aria-live='polite'
              >
                {isResendLocked
                  ? `Didn\u2019t get it? You can request a new code in ${localState.authCodeCounter}s.`
                  : 'Didn\u2019t get it? Request a new code.'}
              </p>

              <div className='flex flex-wrap items-center gap-2.5'>
                <button
                  type='button'
                  disabled={isResendLocked || localState.isLoading}
                  onClick={handleResendButtonClick(OTP_CHANNELS.SMS)}
                  className={`${RESEND_BUTTON_BASE} border border-neutral-250 bg-white text-neutral-750 hover:bg-neutral-125 focus-visible:ring-neutral-350`}
                >
                  {sentViaSms ? (
                    <>
                      <CheckGlyph className='h-4 w-4' />
                      Sent by SMS
                    </>
                  ) : (
                    'Resend code'
                  )}
                </button>

                <button
                  type='button'
                  disabled={isResendLocked || localState.isLoading}
                  onClick={handleResendButtonClick(OTP_CHANNELS.WHATSAPP)}
                  className={`${RESEND_BUTTON_BASE} border border-[#25D366]/40 bg-[#25D366]/10 text-[#0F7A4B] hover:bg-[#25D366]/20 focus-visible:ring-[#25D366]/60`}
                >
                  {sentViaWhatsApp ? (
                    <CheckGlyph className='h-4 w-4' />
                  ) : (
                    <WhatsAppGlyph className='h-[18px] w-[18px] text-[#25D366]' />
                  )}
                  {sentViaWhatsApp ? 'Sent on WhatsApp' : 'Resend via WhatsApp'}
                </button>
              </div>

              {localState.resendError ? (
                <p
                  role='alert'
                  className='text-[13px] leading-[18px] text-red-650'
                >
                  {localState.resendError}
                </p>
              ) : null}
            </div>
          )
          // need to remove nz check when Newzealand NZ sms issue is resolved
          /////////////////////////////////////////////////////////////
        }
      </div>
      {localState.fieldErrors?.verificationCode ? (
        <div className='field-errors'>
          {localState.fieldErrors?.verificationCode}
        </div>
      ) : null}
      {currentOrder.stage === ORDER_STAGE_TYPES.VERIFY_FROM && (
        <div
          style={{ marginTop: 20 }}
          className={
            verificationCode &&
            verificationCode.length === 6 &&
            !localState.isLoading
              ? // ? this.state.isNumberSubmittedAgain
                'nextDetails'
              : // : 'nextDetails wrongCode'
                'nextDetails disabled'
          }
          onClick={handleVerificationCodeSubmit}
        >
          {localState.isLoading ? <img src={nextLoading} alt='' /> : 'Next'}
          {localState.isLoading ? null : (
            <i className='thickcon-Right-Chevron' />
          )}
        </div>
      )}
    </div>
  );
}
